import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { CalendarClock, Check, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { toast } from "./Toast"
import { darfTerminOhneMitarbeiterBuchen } from "@/lib/aufgabeVerknuepfung"
import { MiniDayCalendar, type DayEvent } from "./MiniDayCalendar"
import { kontaktName } from "@/lib/heroSearch"

type SlotCheck = {
  is_free: boolean
  candidates: { partner_id: number; name: string }[]
  message: string
  day_events?: DayEvent[]
}

// Nutzt bestehende /admin/emails/api/email/{id}/calendar-options +
// /admin/emails/api/email/{id}/book Endpoints direkt.
// Keine Business-Logik dupliziert.

interface CalendarOptions {
  categories: { id: number; name: string }[]
  partners: { id: number; name: string }[]
  resources: { id: number; name: string }[]
  monteure: { id: number; name: string }[]
  /** Kalender-Kategorie des Mandanten — dieselbe, die eine automatische
   *  Buchung nimmt. Ist die Vorauswahl; fehlt sie, greift die erste aus HERO. */
  default_category_id?: number | null
  error?: string
}

export function BookingModal({
  open,
  onClose,
  emailId,
  mode = "email",
  proposedSlot,
  prefillSlot,
  prefillTitle,
  prefillDuration,
  defaultTitle,
  tenantSlug,
  heroContactId,
  heroContactName,
  heroProjectId,
  heroProjectLabel,
  invalidateKeys,
}: {
  open: boolean
  onClose: () => void
  emailId: number  // ID des Items (email_id / call_id / meiti_id)
  mode?: "email" | "sipgate" | "meiti"
  proposedSlot?: string | null
  /** Termin, mit dem der Aufrufer das Feld belegen will — schlaegt
   *  `proposedSlot` (die Analyse der eingegangenen Mail). Genutzt von der
   *  Nachfrage nach dem Senden: dort zaehlt der Termin aus der ANTWORT,
   *  nicht der aus der urspruenglichen Anfrage. */
  prefillSlot?: string | null
  /** Titel und Dauer aus einer erkannten Termin-Einladung (#29). */
  prefillTitle?: string | null
  prefillDuration?: string | null
  defaultTitle: string
  tenantSlug: string
  heroContactId: number | null | undefined
  heroContactName?: string | null
  heroProjectId: number | string | null | undefined
  heroProjectLabel?: string | null
  invalidateKeys: (string | number)[][]
}) {
  // v1 fuer email-mode (Welle 2). sipgate/meiti modes bleiben in Welle 5/6.
  const optionsUrl =
    mode === "sipgate" ? `/api/v1/sipgate/${emailId}/calendar-options` :
    mode === "meiti" ? `/api/v1/calls/${emailId}/calendar-options` :
    `/api/v1/emails/${emailId}/calendar-options`
  const bookUrl =
    mode === "sipgate" ? `/api/v1/sipgate/${emailId}/hero-book` :
    mode === "meiti" ? `/api/v1/calls/${emailId}/book` :
    `/api/v1/emails/${emailId}/book`
  const searchContactsUrl = "/api/v1/contacts/search"
  const searchProjectsUrl = "/api/v1/projects/search"
  // setContactUrl/assignProjectUrl: email-mode nutzt PATCH /api/v1/emails/{id}/hero-link.
  // Die HeroLinkRow unten stoesst dann statt POST mit Body {hero_contact_id:...}
  // einen PATCH mit {contact_id: X} ab. sipgate/meiti modes bleiben unaendert.
  const setContactUrl =
    mode === "sipgate" ? `/api/v1/sipgate/${emailId}/hero-link` :
    mode === "meiti" ? `/api/v1/calls/${emailId}/hero-link` :
    `/api/v1/emails/${emailId}/hero-link`
  const assignProjectUrl =
    mode === "sipgate" ? `/api/v1/sipgate/${emailId}/hero-link` :
    mode === "meiti" ? `/api/v1/calls/${emailId}/hero-link` :
    `/api/v1/emails/${emailId}/hero-link`
  const qc = useQueryClient()
  const [slot, setSlot] = useState("")
  const [slotSecondary, setSlotSecondary] = useState("")
  const [doubleSlot, setDoubleSlot] = useState(false)
  const [duration, setDuration] = useState("2")
  const [title, setTitle] = useState(defaultTitle)
  const [description, setDescription] = useState("")
  const [categoryId, setCategoryId] = useState<string>("")
  // Hat jemand die Terminart von Hand gewaehlt? Dann bleibt sie — sonst folgt
  // sie dem Vorgang (Wartung / „Angebot angenommen" → Umsetzung).
  const [kategorieAngefasst, setKategorieAngefasst] = useState(false)
  const [partnerIds, setPartnerIds] = useState<number[]>([])
  const [resourceIds, setResourceIds] = useState<number[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [slotCheck, setSlotCheck] = useState<SlotCheck | null>(null)
  const [slotCheckSecondary, setSlotCheckSecondary] = useState<SlotCheck | null>(null)
  // Automatische Slot-Suche (freie Termine nach Monteur-Verfügbarkeit +
  // booking_config) — provider-aware (HERO wie fortytools). Wie in Review.
  const [slotOpts, setSlotOpts] = useState<{ start_datetime: string; label: string }[]>([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [slotsShown, setSlotsShown] = useState(false)
  // Termine, die im Text der Mail selbst stehen (#19).
  const [textSlots, setTextSlots] = useState<{ iso: string; label: string }[]>([])
  const loadSlots = async () => {
    if (mode !== "email") return
    setLoadingSlots(true); setSlotsShown(true)
    try {
      // Bei Ganztages-Auswahl gibt es keine Stundendauer — fuer die
      // Slot-Suche mit einem vollen Arbeitstag rechnen.
      const durMin = duration.startsWith("tag") ? 8 * 60 : Math.round(parseFloat(duration || "2") * 60)
      const r = await fetch(`/api/v1/drafts/${emailId}/slot-alternatives?duration_min=${durMin}&count=8`, { credentials: "include" })
      const j = await r.json()
      setSlotOpts(j.items || [])
    } catch { setSlotOpts([]) } finally { setLoadingSlots(false) }
  }
  // "auto" = Server entscheidet via Kontext-Center booking_step_map_by_type
  // "skip" = kein Status-Wechsel
  // numeric string = expliziter Step-Override
  const [stepMode, setStepMode] = useState<string>("auto")

  // Kalender-Options nur laden wenn Modal offen
  const opts = useQuery<CalendarOptions>({
    // Vorgang im Schluessel: wird einer verknuepft, kommt die passende Vorauswahl
    queryKey: ["calendar-options", mode, emailId, heroProjectId ?? null],
    queryFn: async () => {
      const res = await fetch(optionsUrl, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: open,
    staleTime: 1000 * 60 * 5,
  })

  // Default-Werte setzen wenn Modal geöffnet wird
  useEffect(() => {
    if (open) {
      const start = prefillSlot || proposedSlot
      setSlot(start ? toLocalInput(start) : "")
      setSlotOpts([]); setSlotsShown(false)
      setSlotSecondary("")
      setDoubleSlot(false)
      setTitle(prefillTitle || defaultTitle)
      setDuration(prefillDuration || "2")
      setErr(null)
      setStepMode("auto")
    }
  }, [open, proposedSlot, prefillSlot, defaultTitle, prefillTitle, prefillDuration])

  // Ist ein Vorgang verknuepft, heisst der Termin nach ihm — nicht nach dem
  // Kontakt (#41, Marlene am 26.08.2026: „bitte IMMER in allen Sektionen wo
  // Termine erstellt werden und bereits Projkekte erstellt sind, der
  // Projektname als Termin Titel automatische eintragen - NICHT den Name").
  //
  // 🔑 Bewusst HIER und nicht bei den Aufrufern. Den Titel bestimmten bisher
  // sechs Stellen selbst (`SipgateCallsList`, `SipgateCallDetail`,
  // `MeitiCallsList`, `MeitiCallDetail`, `EmailsList`, `EmailDetail`), und
  // jede setzte den Kontaktnamen. Sie liefern ihn weiter als `defaultTitle` —
  // aber nur noch als Rueckfall fuer den Fall ohne Vorgang.
  //
  // Die Regel selbst steht im Backend (`services.booking.termin_titel`, aus
  // Ticket #4). Wir fragen sie ab, statt sie nachzubauen: sonst haette der
  // Kalender fuer automatische und von Hand angelegte Termine zwei
  // verschiedene Titel-Regeln.
  //
  // 🔴 `prefillTitle` sticht: eine erkannte Termin-Einladung (#29) bringt
  // einen echten Titel mit, und der ist naeher dran als jede Ableitung.
  useEffect(() => {
    if (!open || prefillTitle || !heroProjectId || !tenantSlug) return
    let abgebrochen = false
    const ctrl = new AbortController()
    fetch(
      `/api/v1/projects/${encodeURIComponent(String(heroProjectId))}/termin-titel`
      + `?tenant=${encodeURIComponent(tenantSlug)}`,
      { credentials: "include", signal: ctrl.signal },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { titel?: string } | null) => {
        const vorschlag = (j?.titel || "").trim()
        if (abgebrochen || !vorschlag) return
        // Nur setzen, solange niemand getippt hat. Sonst ueberschriebe eine
        // langsame Antwort dem Nutzer sein Feld unter den Haenden.
        setTitle((jetzt) => (jetzt === defaultTitle ? vorschlag : jetzt))
      })
      .catch(() => { /* kein Vorschlag ist besser als ein Fehler im Dialog */ })
    return () => { abgebrochen = true; ctrl.abort() }
  }, [open, heroProjectId, tenantSlug, prefillTitle, defaultTitle])

  // Termine aus dem Mailtext holen und den ersten vorbelegen (#19).
  //
  // Bis hierher blieb TERMIN leer, sobald die Mail keinen `proposed_slot`
  // hatte — und den fuellt nur die Analyse EINGEHENDER Mails. Wer eine
  // Terminbestaetigung schrieb und danach den Blocker anlegen wollte, tippte
  // die Uhrzeit aus dem eigenen Text ab.
  //
  // Vorbelegt wird nur ein LEERES Feld: ein KI-Vorschlag und erst recht eine
  // Eingabe von Hand haben Vorrang.
  useEffect(() => {
    setTextSlots([])
    if (!open || mode !== "email") return
    const ctrl = new AbortController()
    fetch(`/api/v1/emails/${emailId}/termin-vorschlaege`,
          { credentials: "include", signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const items = (j?.items || []) as { iso: string; label: string }[]
        if (items.length === 0) return
        setTextSlots(items)
        setSlot((bisher) => bisher || toLocalInput(items[0].iso))
      })
      .catch(() => { /* ohne Vorschlaege ist das Feld eben leer */ })
    return () => ctrl.abort()
  }, [open, mode, emailId])

  // Slot-Check (Frei/Belegt + Mini-Kalender) — nur fuer mode="email" verfuegbar
  useEffect(() => {
    setSlotCheck(null)
    if (!open || mode !== "email" || !slot) return
    const dur = duration.startsWith("tag") ? 8 * 60 : (Math.round(parseFloat(duration) * 60) || 60)
    const startISO = new Date(slot).toISOString().slice(0, 19)
    const params = new URLSearchParams({ start: startISO, duration_min: String(dur) })
    const ctrl = new AbortController()
    fetch(`/api/v1/drafts/${emailId}/slot-check?${params.toString()}`,
          { credentials: "include", signal: ctrl.signal })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (j) setSlotCheck(j) })
      .catch(() => { /* ignore */ })
    return () => ctrl.abort()
  }, [open, mode, emailId, slot, duration])

  useEffect(() => {
    setSlotCheckSecondary(null)
    if (!open || !doubleSlot || mode !== "email" || !slotSecondary) return
    const dur = duration.startsWith("tag") ? 8 * 60 : (Math.round(parseFloat(duration) * 60) || 60)
    const startISO = new Date(slotSecondary).toISOString().slice(0, 19)
    const params = new URLSearchParams({ start: startISO, duration_min: String(dur) })
    const ctrl = new AbortController()
    fetch(`/api/v1/drafts/${emailId}/slot-check?${params.toString()}`,
          { credentials: "include", signal: ctrl.signal })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (j) setSlotCheckSecondary(j) })
      .catch(() => { /* ignore */ })
    return () => ctrl.abort()
  }, [open, doubleSlot, mode, emailId, slotSecondary, duration])

  // Pipeline-Steps fuer Override-Dropdown laden (nur wenn an HERO-Projekt gebunden)
  const pipeline = useQuery<{ pipeline_steps: { id: number; name: string }[] }>({
    queryKey: ["pipeline-steps", tenantSlug],
    queryFn: async () => {
      const r = await fetch(`/api/v1/context/pipeline?tenant=${encodeURIComponent(tenantSlug)}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: open && !!heroProjectId && !!tenantSlug,
    staleTime: 1000 * 60 * 5,
  })

  // fortytools: keine HERO-Kategorien/Ressourcen — Buchung wird ein
  // portal-eigener Termin (SSOT), das Büro überträgt ihn nach fortytools.
  const isForty = (opts.data as any)?.provider === "fortytools"

  useEffect(() => {
    if (!opts.data?.categories?.length || kategorieAngefasst) return
    // Die Kategorie des Mandanten gewinnt — sonst bucht das Fenster in einer
    // anderen Kategorie als jede automatische Buchung. Nur wenn sie fehlt oder
    // HERO sie nicht (mehr) kennt, faellt es auf die erste Kategorie zurueck.
    const vorgabe = opts.data.default_category_id
    const bekannt = vorgabe != null && opts.data.categories.some((c) => c.id === vorgabe)
    setCategoryId(String(bekannt ? vorgabe : opts.data.categories[0].id))
  }, [opts.data, kategorieAngefasst])

  const book = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        // "email"-mode nutzt "slot" | "sipgate/meiti" nutzen "start_datetime"
        ...(mode === "email"
          ? { slot: slot ? new Date(slot).toISOString() : undefined }
          : { start_datetime: slot ? new Date(slot).toISOString() : null }),
        // "tag3" -> 3 ganze Tage. Ohne Uhrzeit: der Server setzt den Start
        // auf 00:00 und rechnet das Ende exklusiv.
        ...(duration.startsWith("tag")
          ? { ganztags_tage: parseInt(duration.slice(3), 10) }
          : { duration_hours: parseFloat(duration) }),
        title,
        description,
        category_id: categoryId ? parseInt(categoryId) : undefined,
        partner_ids: partnerIds,
        resource_ids: resourceIds,
      }
      // Doppel-Termin (nur email-mode): zweiter Slot als parallel-Vorschlag,
      // beide werden als HERO-Events angelegt + IDs in executed_event_ids gespeichert
      if (mode === "email" && doubleSlot && slotSecondary) {
        body.slot_secondary = new Date(slotSecondary).toISOString()
      }
      // Step-Override (nur fuer email-mode + projekt-bound). "auto" = nicht senden.
      if (mode === "email" && heroProjectId && stepMode !== "auto") {
        body.step_id = stepMode === "skip" ? "skip" : parseInt(stepMode)
      }
      const res = await fetch(bookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      return data
    },
    onSuccess: (d) => {
      invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }))
      const n = d?.event_ids?.length || (d?.event_id_secondary ? 2 : 1)
      toast(n > 1 ? `✓ ${n} Termine angelegt (Doppel-Vorschlag)` : "✓ Termin angelegt")
      onClose()
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      toast("Termin-Buchung fehlgeschlagen: " + msg, "error")
    },
  })

  if (!open) return null

  const canBook = !!(heroContactId || heroProjectId)

  return (
    <div
      className="pp-overlay"
      data-portal-modal
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          width: "100%", maxWidth: 560, maxHeight: "85vh", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-[15px] font-semibold flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-primary" /> Termin buchen
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          {/* HERO-Verknuepfungen inline verwalten: Kontakt + Projekt */}
          <div className="space-y-2 p-3 rounded-md border bg-secondary/30">
            <HeroLinkRow
              label="Kontakt"
              currentId={heroContactId || null}
              currentName={heroContactName || null}
              searchUrl={searchContactsUrl}
              assignUrl={setContactUrl}
              tenantSlug={tenantSlug}
              invalidateKeys={invalidateKeys}
              emptyHint="Nicht verknüpft — Name/Tel/Mail suchen"
            />
            <HeroLinkRow
              label="Projekt"
              currentId={heroProjectId || null}
              currentName={heroProjectLabel || null}
              searchUrl={searchProjectsUrl}
              assignUrl={assignProjectUrl}
              tenantSlug={tenantSlug}
              invalidateKeys={invalidateKeys}
              emptyHint="Nicht verknüpft — Projektname suchen"
              isProject
              heroContactId={heroContactId || null}
            />
            {!canBook && (
              <div className="text-[11.5px] text-amber-700">
                Verknüpfe mindestens Kontakt oder Projekt, um buchen zu können.
              </div>
            )}
          </div>
          {err && (
            <div className="p-2.5 rounded-md bg-destructive/10 text-destructive text-[12px]">
              {err}
            </div>
          )}

          {proposedSlot && (
            <div className="p-2.5 rounded-md bg-primary/5 border border-primary/20 text-[12px]">
              <span className="font-semibold">KI-Vorschlag:</span>{" "}
              {new Date(proposedSlot).toLocaleString("de-DE")}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label={doubleSlot ? "Termin-Vorschlag 1" : "Termin"}>
              <input
                type="datetime-local"
                value={slot}
                onChange={(e) => setSlot(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-1.5 text-[13px]"
              />
            </Field>
            <Field label="Dauer">
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-1.5 text-[13px]"
              >
                <option value="1">1 Stunde</option>
                <option value="1.5">1,5 Stunden</option>
                <option value="2">2 Stunden</option>
                <option value="3">3 Stunden</option>
                <option value="4">4 Stunden</option>
                <option value="6">6 Stunden</option>
                <option value="8">8 Stunden (ganzer Tag)</option>
                {/* Ganztaegig ueber mehrere Tage (#13). Die Liste endete
                    vorher bei 8 Stunden — mehrtaegige Termine liessen sich
                    ueberhaupt nicht anlegen. */}
                <option value="tag2">2 Tage (ganztägig)</option>
                <option value="tag3">3 Tage (ganztägig)</option>
                <option value="tag4">4 Tage (ganztägig)</option>
                <option value="tag5">5 Tage (ganztägig)</option>
                <option value="tag10">10 Tage (ganztägig)</option>
              </select>
            </Field>
          </div>

          {/* Termine, die im Mailtext stehen (#19) — der erste ist vorbelegt. */}
          {mode === "email" && textSlots.length > 0 && (
            <div>
              <div className="text-[11.5px] text-muted-foreground" style={{ marginBottom: 6 }}>
                Aus dem Mailtext:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {textSlots.map((t) => {
                  const val = toLocalInput(t.iso)
                  const active = slot === val
                  return (
                    <button
                      key={t.iso}
                      type="button"
                      onClick={() => setSlot(val)}
                      style={{
                        fontSize: 12, padding: "5px 10px", borderRadius: 6, cursor: "pointer",
                        border: "1px solid " + (active ? "var(--accent-color)" : "var(--border-color)"),
                        background: active ? "var(--accent-color)" : "var(--bg-card)",
                        color: active ? "#fff" : "var(--text)",
                      }}
                    >
                      {t.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Automatische Slot-Suche (freie Termine nach Verfügbarkeit) */}
          {mode === "email" && (
            <div>
              <button
                type="button"
                onClick={loadSlots}
                disabled={loadingSlots}
                className="ev2-hero-ibtn"
                style={{ fontSize: 12 }}
              >
                {loadingSlots ? "Suche freie Termine…" : "🔍 Freie Termine vorschlagen"}
              </button>
              {slotsShown && !loadingSlots && slotOpts.length === 0 && (
                <div className="text-[11.5px] text-muted-foreground" style={{ marginTop: 6 }}>
                  Keine freien Termine im Suchfenster gefunden.
                </div>
              )}
              {slotOpts.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                  {slotOpts.map((s) => {
                    const val = s.start_datetime.slice(0, 16)
                    const active = slot === val
                    return (
                      <button
                        key={s.start_datetime}
                        type="button"
                        onClick={() => setSlot(val)}
                        style={{
                          fontSize: 12, padding: "5px 10px", borderRadius: 6, cursor: "pointer",
                          border: "1px solid " + (active ? "var(--accent-color)" : "var(--border-color)"),
                          background: active ? "var(--accent-color)" : "var(--bg-card)",
                          color: active ? "#fff" : "var(--text)",
                        }}
                      >
                        {s.label}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Mini-Kalender fuer Slot 1 — zeigt Verfuegbarkeit + Tagesansicht */}
          {mode === "email" && slot && slotCheck?.day_events && (
            <MiniDayCalendar
              slotStart={new Date(slot).toISOString().slice(0, 19)}
              slotDurationMin={Math.round(parseFloat(duration) * 60) || 60}
              events={slotCheck.day_events}
              isFree={slotCheck.is_free}
            />
          )}

          {/* Doppel-Termin-Toggle: zwei Vorschlaege an verschiedenen Tagen */}
          {mode === "email" && (
            <div className="flex items-center gap-2 text-[12px]">
              <input
                type="checkbox"
                id="double-slot-toggle"
                checked={doubleSlot}
                onChange={(e) => setDoubleSlot(e.target.checked)}
              />
              <label htmlFor="double-slot-toggle" className="cursor-pointer">
                Zweiten Termin als alternativen Vorschlag anbieten (beide werden als Blocker angelegt)
              </label>
            </div>
          )}

          {doubleSlot && mode === "email" && (
            <>
              <Field label="Termin-Vorschlag 2">
                <input
                  type="datetime-local"
                  value={slotSecondary}
                  onChange={(e) => setSlotSecondary(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-1.5 text-[13px]"
                />
              </Field>
              {slotSecondary && slotCheckSecondary?.day_events && (
                <MiniDayCalendar
                  slotStart={new Date(slotSecondary).toISOString().slice(0, 19)}
                  slotDurationMin={Math.round(parseFloat(duration) * 60) || 60}
                  events={slotCheckSecondary.day_events}
                  isFree={slotCheckSecondary.is_free}
                />
              )}
            </>
          )}

          <Field label="Titel">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-1.5 text-[13px]"
            />
          </Field>

          <Field label="Beschreibung">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-md border bg-background px-3 py-1.5 text-[13px]"
            />
          </Field>

          {!isForty && (
          <Field label="Kategorie">
            {opts.isLoading ? (
              <div className="text-[11.5px] text-muted-foreground">Lädt Kategorien...</div>
            ) : (
              <select
                value={categoryId}
                onChange={(e) => { setCategoryId(e.target.value); setKategorieAngefasst(true) }}
                className="w-full rounded-md border bg-background px-3 py-1.5 text-[13px]"
              >
                <option value="">– Kategorie –</option>
                {opts.data?.categories?.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
          </Field>
          )}
          {isForty && (
            <div className="text-[11.5px] text-muted-foreground">
              fortytools: wird als Termin in unserem Kalender angelegt (⚠ offen), Büro überträgt nach fortytools.
            </div>
          )}

          {(opts.data?.partners?.length || opts.data?.monteure?.length) && (
            <Field label="Mitarbeiter">
              <div className="max-h-32 overflow-y-auto border rounded-md p-2 space-y-1">
                {[...(opts.data?.partners || []), ...(opts.data?.monteure || [])]
                  .filter((p, i, arr) => arr.findIndex((x) => x.id === p.id || x.name === p.name) === i)
                  .map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={partnerIds.includes(p.id)}
                        onChange={(e) => setPartnerIds(
                          e.target.checked
                            ? [...partnerIds, p.id]
                            : partnerIds.filter((x) => x !== p.id),
                        )}
                      />
                      {p.name}
                    </label>
                  ))}
              </div>
            </Field>
          )}

          {(opts.data?.resources?.length ?? 0) > 0 && (
            <Field label="Ressourcen">
              <div className="max-h-24 overflow-y-auto border rounded-md p-2 space-y-1">
                {opts.data!.resources.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={resourceIds.includes(r.id)}
                      onChange={(e) => setResourceIds(
                        e.target.checked
                          ? [...resourceIds, r.id]
                          : resourceIds.filter((x) => x !== r.id),
                      )}
                    />
                    {r.name}
                  </label>
                ))}
              </div>
            </Field>
          )}

          {mode === "email" && heroProjectId && (
            <Field label="Status nach Buchung">
              <select
                value={stepMode}
                onChange={(e) => setStepMode(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-1.5 text-[13px]"
              >
                <option value="auto">Auto (laut Pipeline-Konfig)</option>
                <option value="skip">Status nicht aendern</option>
                {(pipeline.data?.pipeline_steps || []).map((s) => (
                  <option key={s.id} value={String(s.id)}>→ {s.name}</option>
                ))}
              </select>
              <div className="text-[11px] text-muted-foreground mt-1">
                Auto nutzt die Konfig im Kontext-Center → Pipeline pro Projekttyp
                (Erstgespraech vs. Umsetzungstermin).
              </div>
            </Field>
          )}

          <div className="text-[11.5px] text-muted-foreground">
            {heroProjectId
              ? "Wird an HERO-Projekt verknüpft."
              : heroContactId
              ? "Termin ohne Projekt-Verknüpfung (Kontakt-Termin)."
              : null}
          </div>
        </div>

        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/70"
          >
            Abbrechen
          </button>
          <button
            disabled={!canBook || !slot || book.isPending}
            onClick={async () => {
              setErr(null)
              // #148 (Paul: „nur warnen"): ohne Auswahl sucht der Server einen
              // freien Monteur — findet er keinen, entsteht der Termin ohne.
              if (!(await darfTerminOhneMitarbeiterBuchen(partnerIds.length + resourceIds.length, true))) return
              book.mutate()
            }}
            className={cn(
              "text-[12.5px] font-medium px-4 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-1.5",
              (!canBook || !slot || book.isPending) && "opacity-50 cursor-not-allowed",
            )}
          >
            <Check className="w-3.5 h-3.5" />
            {book.isPending ? "Bucht..." : "Termin anlegen"}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Inline-Linker fuer Kontakt oder Projekt im Booking-Modal. Zeigt aktuellen Stand
 *  und erlaubt Suche + direkte Verknuepfung. Nach Erfolg werden die Parent-Queries
 *  invalidiert, wodurch die neuen IDs zurueckfliessen. */
function HeroLinkRow({
  label, currentId, currentName, searchUrl, assignUrl, tenantSlug, invalidateKeys,
  emptyHint, isProject, heroContactId,
}: {
  label: string
  currentId: number | string | null
  currentName: string | null
  searchUrl: string
  assignUrl: string
  tenantSlug: string
  invalidateKeys: (string | number)[][]
  emptyHint: string
  isProject?: boolean
  heroContactId?: number | null
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [q, setQ] = useState("")
  // `company_name`: Firmenkontakte haben in HERO weder Vor- noch Nachnamen (#52).
  type Hit = { id: number; first_name?: string; last_name?: string; company_name?: string; email?: string; phone?: string; project_nr?: string; project_name?: string; name?: string; code?: string | null }
  const [hits, setHits] = useState<Hit[]>([])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!editing || q.trim().length < 2) { setHits([]); return }
    const t = setTimeout(async () => {
      try {
        // v1: GET mit Query-Params, kein Body. contactId-Filter wird
        // serverseitig nicht angewendet (analog admin-Endpoint vorher).
        void heroContactId
        const url = `${searchUrl}?tenant=${encodeURIComponent(tenantSlug)}&q=${encodeURIComponent(q.trim())}`
        const res = await fetch(url, { credentials: "include" })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const d = await res.json()
        const arr = Array.isArray(d.items) ? d.items : []
        setHits(arr.slice(0, 8))
      } catch {
        // Bewusst still: der Nutzer tippt hier weiter. Wichtig ist nur, dass
        // keine Liste vom vorigen Suchwort stehen bleibt (#52).
        setHits([])
      }
    }, 250)
    return () => clearTimeout(t)
  }, [q, editing, tenantSlug, searchUrl, isProject, heroContactId])

  const pick = async (h: Hit) => {
    setBusy(true)
    try {
      // v1 hero-link PATCH: {contact_id, contact_name} oder {project_id}.
      // alte admin-Endpoints (sipgate/meiti modes): {hero_contact_id, ...}.
      const isV1 = assignUrl.includes("/api/v1/")
      const body = isV1
        ? (isProject
            ? { project_id: h.id }
            : { contact_id: h.id, contact_name: kontaktName(h) })
        : (isProject
            ? { hero_project_id: h.id }
            : { hero_contact_id: h.id, hero_contact_name: kontaktName(h) })
      const res = await fetch(assignUrl, {
        method: isV1 ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        toast(`${label} verknüpfen fehlgeschlagen: ${d.error || res.status}`, "error")
        return
      }
      toast(`✓ ${label} verknüpft`)
      invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }))
      setEditing(false); setQ(""); setHits([])
    } finally { setBusy(false) }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11.5px]">
          <span className="text-muted-foreground">{label}:</span>{" "}
          {currentId ? (
            <span className="font-semibold">{currentName || `#${currentId}`}</span>
          ) : (
            <span className="text-muted-foreground italic">{emptyHint}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setEditing(!editing)}
          className="text-[11px] text-primary hover:underline"
        >
          {editing ? "Abbrechen" : (currentId ? "ändern" : "verknüpfen")}
        </button>
      </div>
      {editing && (
        <div style={{ marginTop: 6, position: "relative" }}>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={isProject ? "Projektname suchen…" : "Name / Telefon / E-Mail suchen…"}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-[12.5px]"
            disabled={busy}
          />
          {hits.length > 0 && (
            <div
              style={{
                position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10,
                background: "var(--bg-card)", border: "1px solid var(--border-color)",
                borderRadius: 6, marginTop: 2, maxHeight: 220, overflowY: "auto",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              {hits.map((h) => {
                const displayName = isProject
                  ? [h.code || h.project_nr, h.project_name || h.name].filter(Boolean).join(" · ")
                  : (h.name && !h.first_name && !h.last_name && !h.company_name
                      ? h.name
                      : kontaktName(h))
                const sub = isProject ? "" : [h.email, h.phone].filter(Boolean).join(" · ")
                return (
                  <button
                    key={h.id}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); pick(h) }}
                    className="hover:bg-secondary"
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: "6px 10px", border: "none", background: "none",
                      cursor: "pointer", fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{displayName || `#${h.id}`}</div>
                    {sub && <div style={{ color: "var(--text-muted)", fontSize: 12 }}>{sub}</div>}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">{label}</label>
      {children}
    </div>
  )
}

/** "2026-04-20T14:30:00" (ohne TZ) → "2026-04-20T14:30" für <input type="datetime-local">. */
function toLocalInput(iso: string): string {
  try {
    const d = new Date(iso)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    const h = String(d.getHours()).padStart(2, "0")
    const min = String(d.getMinutes()).padStart(2, "0")
    return `${y}-${m}-${day}T${h}:${min}`
  } catch {
    return ""
  }
}
